/**
 * EditIndividualObjectiveDialog Component
 *
 * Dialog for editing individual objective/task details
 */

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import { useOKRDashboardContext } from '../OKRDashboardContext';
import { okrApi } from '../../../services/okrApi';
import { COMPLETION_STATUS_OPTIONS, getStatusColor } from '../utils/scoreUtils';

export const EditIndividualObjectiveDialog: React.FC = () => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { data, dialogs } = useOKRDashboardContext();
    const { setSuccess, setError, softRefetchData } = data;
    const {
        dialogs: dialogState,
        formState,
        closeEditIndObjDialog,
    } = dialogs;

    const objective = formState.selectedIndObjForEdit;

    // Local state for form fields
    const [title, setTitle] = useState('');
    const [startDate, setStartDate] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [status, setStatus] = useState('not_started');
    const [comment, setComment] = useState('');

    // Initialize form when dialog opens
    useEffect(() => {
        if (objective) {
            setTitle(objective.title || objective.obj_title || '');
            setStartDate(objective.start_date ? objective.start_date.split('T')[0] : '');
            setDueDate(objective.due_date ? objective.due_date.split('T')[0] : '');
            setStatus(objective.status || objective.obj_status || 'not_started');
            setComment(objective.comment || '');
        }
    }, [objective]);

    const handleSubmit = async () => {
        if (!objective?.id && !objective?.obj_id) return;
        if (!title.trim()) return;

        const objId = objective.id || objective.obj_id;

        try {
            await okrApi.updateIndividualObjective(
                objId,
                title.trim(),
                startDate || undefined,
                dueDate || undefined,
                comment || undefined,
                status || undefined
            );
            setSuccess('Objective updated successfully');
            closeEditIndObjDialog();
            if (softRefetchData) {
                await softRefetchData();
            }
        } catch (err: any) {
            setError(err.message || 'Failed to update objective');
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            handleSubmit();
        }
    };

    const handleClose = () => {
        closeEditIndObjDialog();
        setTitle('');
        setStartDate('');
        setDueDate('');
        setStatus('not_started');
        setComment('');
    };

    return (
        <Dialog
            open={dialogState.editIndObjDialog}
            onClose={handleClose}
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: isDarkMode ? '#1e293b' : '#fff',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                    minHeight: '480px',
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    borderBottom: '1px solid',
                    borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                    pb: 2,
                }}
            >
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Edit sx={{ color: '#fff', fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                    Edit Objective
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ pt: '24px !important', pb: 2, overflowY: 'auto' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                    <TextField
                        label="Objective Title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        onKeyDown={handleKeyDown}
                        fullWidth
                        required
                        autoFocus
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                            },
                        }}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="Start Date"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                                },
                            }}
                        />
                        <TextField
                            label="Due Date"
                            type="date"
                            value={dueDate}
                            onChange={(e) => setDueDate(e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                                },
                            }}
                        />
                    </Box>
                    <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            label="Status"
                            sx={{
                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                                color: getStatusColor(status),
                                fontWeight: 600,
                            }}
                        >
                            {COMPLETION_STATUS_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value} sx={{ color: opt.color }}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Notes / Comments"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Add any notes or comments about this objective..."
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                            },
                        }}
                    />
                    <Typography
                        variant="caption"
                        sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                    >
                        Tip: Press Ctrl+Enter to save quickly
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    py: 2,
                    borderTop: '1px solid',
                    borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                }}
            >
                <Button
                    onClick={handleClose}
                    sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!title.trim()}
                    sx={{
                        background: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)',
                        fontWeight: 600,
                        px: 3,
                        '&:hover': {
                            background: 'linear-gradient(135deg, #16a34a 0%, #059669 100%)',
                        },
                    }}
                >
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditIndividualObjectiveDialog;
